import type { FC } from 'react';
import { Fragment } from 'react';
import {
  StyledBox,
  StyledContainer,
  StyledFooter,
  StyledParagraph,
} from './footer.styles';
import { StyledHeading4 } from '../Typography/typography.styles';
import { StyledDivider } from '../Divider/divider.styles';
import { Logo } from '../Logo/logo.component';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { settingsQueryOptions } from '../../queries/settings.query';
import { FbIcon, IgIcon, MailIcon, PhoneIcon } from '../icons';
import { getSeoLabel } from '../../consts/general.const';
import type { Box } from './footer.type';
import { PATH } from '../../app.type';

export const Footer: FC = () => {
  const { data } = useQuery(settingsQueryOptions());
  const boxes: Box[] = [
    {
      key: 'contact-info-block',
      links: [
        {
          icon: MailIcon,
          key: 'mail',
          text: data?.email ?? '',
          title: 'Napisz do nas',
          to: `mailto:${data?.email}`,
        },
        {
          icon: PhoneIcon,
          key: 'phone',
          text: data?.phoneNumber ?? '',
          title: 'Zadzwoń do nas',
          to: `tel:${data?.phoneNumber}`,
        },
      ],
      title: 'Skontaktuj się z nami',
    },
    {
      key: 'socials-block',
      links: [
        {
          icon: FbIcon,
          key: 'facebook',
          text: 'Facebook',
          title: 'Zobacz co u nas na FB',
          to: data?.socialMedia?.[0]?.url ?? '',
        },
        {
          icon: IgIcon,
          key: 'instagram',
          text: 'Instagram',
          title: 'Zobacz co u nas na IG',
          to: data?.socialMedia?.[1]?.url ?? '',
        },
      ],
      title: 'Dowiedz się co u nas',
    },
    {
      key: 'links-block',
      links: [
        {
          key: 'contact',
          text: 'Kontakt',
          title: 'Busem w Trasę - Kontakt',
          to: PATH.CONTACT,
        },
        {
          key: 'lease-terms',
          text: 'Warunki wynajmu',
          title: 'Busem w Trasę - Warunki wynajmu',
          to: PATH.LEASE_TERMS,
        },
        {
          key: 'cookies-policy',
          text: 'Polityka Prywatności',
          title: 'Busem w Trasę - Polityka Prywatności',
          to: PATH.COOKIE_POLICY,
        },
      ],
      title: 'Przydatne informacje',
    },
  ];

  return (
    <StyledFooter>
      <StyledContainer>
        <Logo position="vertical" />
        {boxes.map((box, idx) => {
          const { key, links, title } = box;

          return (
            <Fragment key={key}>
              <StyledBox>
                <StyledHeading4>{title}</StyledHeading4>
                {links.map((link) => {
                  const { icon: Icon, key: linkKey, text, title, to } = link;
                  const isExternal = /^(https|mailto:|tel:)/.test(to);

                  return (
                    <StyledParagraph key={`${key}-${linkKey}`}>
                      {Icon ? <Icon /> : null}
                      {isExternal ? (
                        <a
                          href={to}
                          title={getSeoLabel(title)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {text}
                        </a>
                      ) : (
                        <Link to={to}>{text}</Link>
                      )}
                    </StyledParagraph>
                  );
                })}
              </StyledBox>
              {idx !== boxes.length - 1 ? <StyledDivider /> : null}
            </Fragment>
          );
        })}
      </StyledContainer>
    </StyledFooter>
  );
};
